import {inject, Injectable, signal, PLATFORM_ID, DOCUMENT} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {isKnownPalette} from '../theme/palettes';

/**
 * Палитры темы оформления (пресеты из tailwind.css).
 *
 * Два слоя:
 *  - site-палитра: имена из api/Settings, применяются ко ВСЕМ посетителям
 *    через атрибуты data-palette-light/dark на <html>. Выставляются при
 *    загрузке настроек — в SSR тоже, поэтому атрибуты попадают в HTML.
 *  - admin-превью: localStorage site-palette-*; выставляется только
 *    админом из «Оформление» и ранним скриптом в index.html.
 *
 * Приоритет: превью админа > дефолт сайта > дефолтная палитра (без атрибута).
 */
@Injectable({providedIn: 'root'})
export class PaletteService {

  static readonly previewKeyLight = 'site-palette-light';
  static readonly previewKeyDark = 'site-palette-dark';

  /** Дефолты сайта из настроек ('' = базовая палитра без атрибута). */
  readonly siteLight = signal('');
  readonly siteDark = signal('');

  /** Активные превью админа (null = превью выключено). */
  readonly previewLight = signal<string | null>(null);
  readonly previewDark = signal<string | null>(null);

  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly document = inject(DOCUMENT);

  /**
   * Сохранить сайт-дефолты и применить атрибуты.
   * Вызывается при загрузке api/Settings (см. AppComponent) — на SSR тоже.
   */
  applySite(light: string, dark: string): void {
    this.siteLight.set(isKnownPalette(light, 'light') ? light : '');
    this.siteDark.set(isKnownPalette(dark, 'dark') ? dark : '');
    this.applyAttrs();
  }

  /** Текущее имя палитры с учётом превью. */
  current(mode: 'light' | 'dark'): string {
    return mode === 'light'
      ? (this.previewLight() ?? this.siteLight())
      : (this.previewDark() ?? this.siteDark());
  }

  /** Админское превью: атрибут + localStorage (переживает перезагрузку). */
  preview(mode: 'light' | 'dark', name: string): void {
    if (!this.isBrowser) return;
    if (!isKnownPalette(name, mode)) return;

    const key = mode === 'light'
      ? PaletteService.previewKeyLight
      : PaletteService.previewKeyDark;

    if (name === '') {
      localStorage.removeItem(key);
      (mode === 'light' ? this.previewLight : this.previewDark).set(null);
    } else {
      localStorage.setItem(key, name);
      (mode === 'light' ? this.previewLight : this.previewDark).set(name);
    }
    this.applyAttrs();
  }

  /** Сбросить превью — вернуться к сайт-дефолтам. */
  clearPreview(): void {
    if (!this.isBrowser) return;
    localStorage.removeItem(PaletteService.previewKeyLight);
    localStorage.removeItem(PaletteService.previewKeyDark);
    this.previewLight.set(null);
    this.previewDark.set(null);
    this.applyAttrs();
  }

  /** Прочитать превью из localStorage (выставлено ранним скриптом). */
  restorePreview(): void {
    if (!this.isBrowser) return;
    const l = localStorage.getItem(PaletteService.previewKeyLight);
    const d = localStorage.getItem(PaletteService.previewKeyDark);
    if (l && isKnownPalette(l, 'light')) this.previewLight.set(l);
    if (d && isKnownPalette(d, 'dark')) this.previewDark.set(d);
  }

  private applyAttrs(): void {
    // setAttribute, not dataset: the SSR DOM (domino) has no dataset (#1099)
    this.setAttr('data-palette-light', this.previewLight() ?? this.siteLight());
    this.setAttr('data-palette-dark', this.previewDark() ?? this.siteDark());
  }

  private setAttr(name: string, value: string): void {
    const el = this.document.documentElement;
    if (value) el.setAttribute(name, value);
    else el.removeAttribute(name);
  }
}
